import { z } from 'zod';
// Standard film, television and advertising production departments and roles.
// Users can add their own via "+ Add New"; custom entries are saved to their account.
export const DEPARTMENTS = {
  'Production': ['Director', 'Producer', 'Executive Producer', 'Line Producer', 'Production Manager', 'Production Coordinator', 'Production Assistant'],
  'Direction': ['First Assistant Director', 'Second Assistant Director', 'Third Assistant Director', 'Script Supervisor', 'Runner'],
  'Camera': ['Director of Photography', 'Camera Operator', 'First Assistant Camera', 'Second Assistant Camera', 'Steadicam Operator', 'Digital Imaging Technician', 'Still Photographer'],
  'Lighting': ['Gaffer', 'Best Boy Electric', 'Electrician', 'Lighting Technician'],
  'Grip & Electric': ['Key Grip', 'Best Boy Grip', 'Dolly Grip', 'Grip'],
  'Sound': ['Production Sound Mixer', 'Boom Operator', 'Sound Assistant', 'Sound Designer', 'Sound Editor', 'Composer'],
  'Art Department': ['Production Designer', 'Art Director', 'Set Decorator', 'Set Dresser', 'Prop Master', 'Construction Manager', 'Scenic Artist'],
  'Costume & Wardrobe': ['Costume Designer', 'Costume Supervisor', 'Wardrobe Stylist', 'Dresser'],
  'Hair & Makeup': ['Hair Stylist', 'Makeup Artist', 'Special Effects Makeup Artist'],
  'Editing & Post-Production': ['Editor', 'Assistant Editor', 'Colourist', 'Post-Production Supervisor', 'VFX Supervisor', 'VFX Artist'],
  'Stunts': ['Stunt Coordinator', 'Stunt Performer', 'Safety Supervisor'],
  'Locations': ['Location Manager', 'Location Scout', 'Location Assistant'],
  'Casting': ['Casting Director', 'Casting Associate', 'Extras Casting Coordinator'],
  'Transport': ['Transport Coordinator', 'Driver'],
  'Catering': ['Caterer', 'Craft Services'],
  'Script & Continuity': ['Script Editor', 'Continuity Supervisor', 'Storyboard Artist'],
  'Advertising & Client Services': ['Creative Director', 'Art Director (Advertising)', 'Copywriter', 'Account Director', 'Account Manager', 'Agency Producer', 'Client Services Director'],
};
export const CREW_FIELDS = [
  ['firstName', 'First name', 100], ['lastName', 'Last name', 100], ['contactNumber', 'Contact number', 50],
  ['address', 'Address', 1000], ['email', 'Email', 255],
  ['agentFirstName', 'Agent first name', 100], ['agentLastName', 'Agent last name', 100],
  ['agentContactNumber', 'Agent contact number', 50], ['agentEmail', 'Agent email', 255],
  ['companyName', 'Company name', 255], ['companyEmail', 'Company email', 255], ['companyPhone', 'Company phone number', 50],
];
const shape = Object.fromEntries(CREW_FIELDS.map(([key, , max]) => [key, z.string().trim().max(max)]));
['firstName', 'lastName'].forEach(key => { shape[key] = shape[key].min(1, 'Enter a first and last name.'); });
['email', 'agentEmail', 'companyEmail'].forEach(key => { shape[key] = shape[key].refine(v => !v || z.email().safeParse(v).success, 'Enter a valid email address.'); });
Object.keys(shape).forEach(key => { shape[key] = shape[key].default(''); });
export const crewSchema = z.object({
  ...shape,
  gender: z.enum(['', 'MALE', 'FEMALE', 'OTHER']),
  department: z.string().trim().min(1, 'Choose or add a department.').max(100),
  role: z.string().trim().min(1, 'Choose or add a role.').max(100),
  salaryType: z.enum(['', 'FIXED', 'HOURLY', 'DAILY', 'WEEKLY']),
  rate: z.number().min(0).max(1000000000).nullable(),
  revision: z.number().int().min(0),
});
export const crewDraft = crew => ({
  ...Object.fromEntries(CREW_FIELDS.map(([key]) => [key, crew?.[key] || ''])),
  gender: crew?.gender || '', department: crew?.department || '', role: crew?.role || '',
  salaryType: crew?.salaryType || '', rate: crew?.rate ?? null, revision: crew?.revision || 0,
});
export const crewName = crew => crew ? [crew.firstName, crew.lastName].filter(Boolean).join(' ').trim() : '';
// Merge predefined departments/roles with the user's saved custom entries.
export const mergeOptions = custom => {
  const merged = Object.fromEntries(Object.entries(DEPARTMENTS).map(([dept, roles]) => [dept, [...roles]]));
  (custom || []).forEach(({ department, role }) => {
    if (!department) return;
    if (!merged[department]) merged[department] = [];
    if (role && !merged[department].includes(role)) merged[department].push(role);
  });
  return merged;
};
